import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ServerDiscovery from '../services/ServerDiscovery';
import { SeoHelmet } from '../hooks/useSeo';
import { SEO } from '../seo/routes';
import { getDisplayName, getPlayerId, setDisplayName } from '../utils/playerId';
import { authHeaders, getUser } from '../utils/auth';
import { LANGLE_EVENTS, trackLangle } from '../utils/analytics';
import AccountButton from './AccountButton';

/**
 * Group leaderboard page.
 *
 * URL: /group/<groupId>
 *
 * Behavior:
 *   - Anyone with the link can view the leaderboard.
 *   - The current player (anonymous UUID) can join via a one-field display
 *     name form. After joining their card appears in the list.
 *   - Played members are sorted best→worst by today's score; not-yet-played
 *     members appear at the bottom.
 *   - Auto-refreshes when the tab regains focus / becomes visible (the
 *     moment friends' scores are most likely to have changed). The explicit
 *     "Refresh now" button is kept as a manual fallback.
 */

const SCORE_LABEL = (m) => {
  if (!m.played) return '—';
  if (m.won) return `${m.guessCount}/6`;
  return 'X/6';
};

// Don't issue a fresh /groups/{id} fetch more than once every this-many ms.
// Both `visibilitychange` and `focus` can fire back-to-back when the user
// switches tabs, and we don't want a redundant round-trip in that window.
const MIN_REFRESH_INTERVAL_MS = 5_000;

const NUDGE_REASON_LABEL = {
  already_nudged: 'Already nudged today',
  already_played: 'They already played',
  no_email: "Can't reach them",
  not_member: 'Not a member',
  self: "That's you",
};

const DIFFICULTY_TABS = [
  { value: 'easy', label: 'Easy' },
  { value: 'hard', label: 'Hard' },
];

function formatRelative(ms) {
  if (ms == null) return '';
  // We deliberately don't show seconds: a label that ticks every second is
  // visual noise on a leaderboard that only changes on the minute scale.
  // Anything under one minute reads as "just now".
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60)   return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60)   return `${m}m ago`;
  const h = Math.floor(m / 60);
  return `${h}h ago`;
}

function Group() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [httpBase, setHttpBase] = useState(null);
  const [group, setGroup] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState(getDisplayName());
  const [joining, setJoining] = useState(false);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [difficulty, setDifficulty] = useState('easy');
  const [nudging, setNudging] = useState(null);
  const [nudgeFeedback, setNudgeFeedback] = useState({});
  // Brief flash on the join card when the visitor tries to play before
  // joining — the warning above the Play CTA already explains *why*, but
  // the shake/highlight makes "look up here" unmissable.
  const [joinFlash, setJoinFlash] = useState(false);
  // Stored in a ref because we need its latest value inside event handlers
  // attached once on mount; reading state directly there would close over
  // a stale value.
  const lastFetchAtRef = useRef(0);
  const joinCardRef = useRef(null);
  const nameInputRef = useRef(null);
  // getUser() parses localStorage fresh every call, so it'd otherwise be a
  // new object identity on every render — memoize so effects/callbacks that
  // depend on `user` don't re-fire on every unrelated re-render.
  const user = useMemo(() => getUser(), []);
  const playerId = user?.userId || getPlayerId();

  const fetchGroup = useCallback(async (base, { manual = false } = {}) => {
    if (manual) setRefreshing(true);
    try {
      const res = await fetch(`${base}/groups/${id}?difficulty=${difficulty}`);
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setGroup(data);
      setError('');
      const now = Date.now();
      lastFetchAtRef.current = now;
      setLastUpdatedAt(now);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setLoading(false);
      if (manual) setRefreshing(false);
    }
  }, [id, difficulty]);

  // Fires on mount and again whenever `fetchGroup`'s identity changes (id or
  // difficulty) — only rediscovers httpBase the first time, so switching the
  // Easy/Hard tab is a single round-trip, not a full server rediscovery.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const base = httpBase || await ServerDiscovery.discoverHttpBase();
      if (cancelled) return;
      if (!httpBase) setHttpBase(base);
      await fetchGroup(base);
    })();
    return () => { cancelled = true; };
  }, [fetchGroup]);

  // Auto-refresh on focus / visibility change. Friends' scores most often
  // change while the user is away on another tab playing the daily — coming
  // back to the leaderboard tab is the natural cue to re-fetch. We throttle
  // to MIN_REFRESH_INTERVAL_MS to dedupe the back-to-back focus + visibility
  // events browsers fire on tab switch.
  useEffect(() => {
    if (!httpBase) return undefined;
    const maybeRefresh = () => {
      if (document.visibilityState === 'hidden') return;
      const since = Date.now() - lastFetchAtRef.current;
      if (since < MIN_REFRESH_INTERVAL_MS) return;
      fetchGroup(httpBase);
    };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', maybeRefresh);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', maybeRefresh);
    };
  }, [httpBase, fetchGroup]);

  // Tick the "Updated …" label periodically so it stays accurate. Since
  // formatRelative() resolves to whole minutes (no seconds), a 30s cadence
  // is more than fine — the worst-case staleness of the label is half the
  // tick interval.
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!lastUpdatedAt) return undefined;
    const i = setInterval(() => setTick((n) => n + 1), 30_000);
    return () => clearInterval(i);
  }, [lastUpdatedAt]);

  const isMember = group && group.members.some((m) => m.playerId === playerId);
  const myMember = group && group.members.find((m) => m.playerId === playerId);
  const serverName = (myMember && myMember.displayName) || '';
  const localTrimmed = name.trim();
  const nameDirty = isMember && localTrimmed && localTrimmed !== serverName;

  // /groups/{id}/join is upsert: a fresh playerId joins, an existing one
  // updates display_name. So the same handler covers "join" and "rename".
  // Returns true on success so callers (e.g. the Play CTA intercept) can
  // decide whether to proceed with a follow-up navigation.
  const joinWithName = useCallback(async (rawName) => {
    const trimmed = (rawName || '').trim();
    if (!trimmed || !httpBase) return false;
    setJoining(true);
    setError('');
    try {
      const res = await fetch(`${httpBase}/groups/${id}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ playerId, displayName: trimmed }),
      });
      if (!res.ok) {
        const detail = await res.json().catch(() => ({}));
        throw new Error(detail.detail || `HTTP ${res.status}`);
      }
      setDisplayName(trimmed);
      await fetchGroup(httpBase);
      return true;
    } catch (err) {
      setError(err.message || String(err));
      return false;
    } finally {
      setJoining(false);
    }
  }, [httpBase, id, playerId, fetchGroup]);

  // Signed-in players always play under their account name here — no typed
  // name, no override. Joins (or re-syncs the display name, e.g. after a
  // Google name change) automatically once the group loads. Tracked via a
  // ref, not the `joining` state, so a failed attempt doesn't retry in a
  // tight loop — `joining` flipping back to false would otherwise re-fire
  // this same effect immediately since it's set by joinWithName itself.
  // One retry is allowed per distinct `group` snapshot (e.g. the next
  // focus-triggered refresh), not on every render.
  const autoJoinLockRef = useRef({ inFlight: false, failedFor: null });
  useEffect(() => {
    if (!user?.name || !group) return;
    const already = group.members.find((m) => m.playerId === playerId);
    if (already && already.displayName === user.name) return;
    const lock = autoJoinLockRef.current;
    if (lock.inFlight || lock.failedFor === group) return;
    lock.inFlight = true;
    joinWithName(user.name).then((ok) => {
      lock.inFlight = false;
      lock.failedFor = ok ? null : group;
    });
  }, [user, group, playerId, joinWithName]);

  const sendNudge = useCallback(async (toPlayerId) => {
    if (!httpBase || nudging) return;
    setNudging(toPlayerId);
    try {
      const res = await fetch(`${httpBase}/groups/${id}/nudge`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ toPlayerId, difficulty, playerId }),
      });
      const data = await res.json().catch(() => ({}));
      const label = data.sent ? 'Nudged ✓' : (NUDGE_REASON_LABEL[data.reason] || 'Could not nudge');
      setNudgeFeedback((prev) => ({ ...prev, [toPlayerId]: label }));
    } catch {
      setNudgeFeedback((prev) => ({ ...prev, [toPlayerId]: 'Could not nudge' }));
    } finally {
      setNudging(null);
      setTimeout(() => {
        setNudgeFeedback((prev) => {
          const next = { ...prev };
          delete next[toPlayerId];
          return next;
        });
      }, 4000);
    }
  }, [httpBase, id, difficulty, playerId, nudging]);

  const onJoin = (e) => {
    e?.preventDefault?.();
    joinWithName(name);
  };

  // Click handler for "Play today's Langle". When the visitor is not yet a
  // member we want to make it impossible to miss that playing now means the
  // score won't show up on this leaderboard:
  //   - If they typed a name but never tapped Join, do the join for them
  //     (they clearly meant to participate) and *then* navigate.
  //   - If they typed nothing, intercept: scroll to the join card, focus
  //     the input, flash it briefly, and bounce them out of the navigation.
  //     The persistent warning banner above the CTA explains why.
  const onPlayClick = useCallback(async (e) => {
    if (isMember) return;
    e.preventDefault();
    // Signed in: the auto-join effect above almost always wins this race,
    // but if they click before it lands, just join under the account name
    // directly — no typed-name flow for signed-in players.
    if (user?.name) {
      const ok = await joinWithName(user.name);
      if (ok) navigate(`/langle/${difficulty}`);
      return;
    }
    const trimmed = name.trim();
    if (trimmed) {
      const ok = await joinWithName(trimmed);
      if (ok) navigate(`/langle/${difficulty}`);
      return;
    }
    joinCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    nameInputRef.current?.focus();
    setJoinFlash(true);
    setTimeout(() => setJoinFlash(false), 600);
  }, [isMember, user, name, joinWithName, navigate, difficulty]);

  // Build the share message. Most chat apps render the OG card *plus* the
  // text, so the text needs to stand on its own — when a recipient sees just
  // "https://www.langshazam.com/group/abc123" they have no idea what it is.
  // We name the group, name the inviter (when we know them), and explicitly
  // call out that this is a private leaderboard for today's puzzle. This is
  // also the fallback when the OG card fails to render (which it does in
  // chat apps that don't run the SPA's JS — see og-source/README.md).
  const buildShareMessage = useCallback(() => {
    const url = `${window.location.origin}/group/${id}`;
    const groupLabel = group?.name ? `“${group.name}”` : 'my Langle group';
    const inviter = (myMember?.displayName || localTrimmed || '').trim();
    const opener = inviter
      ? `${inviter} invited you to ${groupLabel} on Langle.`
      : `You're invited to ${groupLabel} on Langle.`;
    const body = group?.puzzleId
      ? `Daily audio language game — see who guesses today's clip fastest. Today: ${group.puzzleId}.`
      : `Daily audio language game — see who guesses today's clip fastest.`;
    return {
      url,
      title: `Join ${groupLabel} on Langle`,
      text: `${opener}\n${body}\n${url}`,
    };
  }, [id, group, myMember, localTrimmed]);

  const onCopyInvite = async () => {
    const { url, title, text } = buildShareMessage();
    try {
      if (navigator.share) {
        // Pass `text` only (no separate `url`) so apps that concatenate
        // title + text + url don't render the link three times. The URL
        // is the last line of `text`, which iOS/Android still detect as
        // a linkable preview.
        await navigator.share({ title, text });
        trackLangle(LANGLE_EVENTS.SHARE, { kind: 'invite' });
        return;
      }
      // Clipboard fallback: copy the same self-describing string the share
      // sheet would have shown, not just the bare URL — pasting into chat
      // should read like an invitation, not like a random link.
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      trackLangle(LANGLE_EVENTS.SHARE, { kind: 'invite' });
    } catch {
      // Soft-fail; the URL is also visible in the input below for manual copy.
      try {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
      } catch { /* user can long-press the URL field */ }
    }
  };

  // Group-specific OG/SEO. We render this at every state (loading, error,
  // success) so social crawlers always get the correct card regardless of
  // whether they wait for the fetch. Base copy and the static group-og.png
  // come from src/seo/routes.js, shared with the prerendered group.html; only
  // the two things a static file can't know — the group's name and its
  // canonical URL — are filled in here. Doing real per-group OG (with the
  // group's actual roster) would need a server-side renderer — deliberate
  // trade-off, see og-source/README.md.
  const groupSeo = (
    <SeoHelmet
      {...SEO.group}
      title={group ? `${group.name} — ${SEO.group.title}` : SEO.group.title}
      path={`/group/${id}`}
    />
  );

  if (loading) {
    return (
      <div className="lng-page">
        {groupSeo}
        <header className="lng-topbar">
          <Link to="/" className="lng-back">←</Link>
          <h1 className="lng-brand">Group</h1>
          <div className="lng-topbar-actions"><AccountButton /></div>
        </header>
        <p className="lng-loading">Loading leaderboard…</p>
      </div>
    );
  }

  if (error && !group) {
    return (
      <div className="lng-page">
        {groupSeo}
        <header className="lng-topbar">
          <Link to="/" className="lng-back">←</Link>
          <h1 className="lng-brand">Group</h1>
          <div className="lng-topbar-actions"><AccountButton /></div>
        </header>
        <div className="lng-error-card">
          <p>{error}</p>
          <Link to="/langle" className="lng-secondary-btn" style={{ display: 'inline-block', marginTop: '1rem' }}>
            Back to Langle
          </Link>
        </div>
      </div>
    );
  }

  const inviteUrl = `${window.location.origin}/group/${id}`;

  return (
    <div className="lng-page">
      {groupSeo}
      <header className="lng-topbar">
        <Link to="/langle" className="lng-back" aria-label="Back to Langle">←</Link>
        <h1 className="lng-brand">{group.name}</h1>
        <div className="lng-topbar-actions"><AccountButton /></div>
      </header>

      <section className="lng-hero">
        <div className="lng-hero-date">Group leaderboard · {group.puzzleId} UTC</div>
        <p className="lng-hero-tagline">{group.members.length} {group.members.length === 1 ? 'member' : 'members'}</p>
      </section>

      <nav className="lng-difficulty-tabs" aria-label="Difficulty">
        {DIFFICULTY_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            className={`lng-difficulty-tab${tab.value === difficulty ? ' is-active' : ''}`}
            onClick={() => setDifficulty(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      {group.activity.length > 0 && (
        <section className="lng-account-section">
          <h3 className="lng-modal-subtitle">Recent activity</h3>
          <ul className="lng-account-list">
            {group.activity.map((a, i) => (
              <li key={`${a.playerId}-${a.ts}-${i}`}>
                <span className="lng-history-row">
                  <span>{a.displayName} · {a.difficulty} · {a.won ? `${a.guessCount}/6` : 'X/6'}</span>
                  <strong>{formatRelative(a.ts * 1000)}</strong>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="lng-invite-card">
        <div className="lng-invite-label">Invite link</div>
        <div className="lng-invite-row">
          <input className="lng-invite-input" value={inviteUrl} readOnly onFocus={(e) => e.target.select()} />
          <button className="lng-share-btn" onClick={onCopyInvite}>
            {copied ? '✓ Copied' : 'Copy'}
          </button>
        </div>
      </section>

      {user?.name ? (
        <p className="lng-join-hint" style={{ textAlign: 'center' }}>
          {isMember ? <>Playing as <strong>{user.name}</strong></> : (joining ? 'Joining…' : '')}
        </p>
      ) : (
        <form
          ref={joinCardRef}
          className={`lng-join-card${joinFlash ? ' is-shaking' : ''}${!isMember ? ' is-required' : ''}`}
          onSubmit={onJoin}
        >
          <label htmlFor="lng-join-name" className="lng-join-label">
            {isMember ? 'Your name' : 'Join to appear on the leaderboard'}
          </label>
          <div className="lng-input-row">
            <input
              id="lng-join-name"
              ref={nameInputRef}
              className="lng-input"
              placeholder="Your display name (e.g. Kaan)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="off"
              maxLength={32}
            />
            <button
              className="lng-submit"
              type="submit"
              disabled={joining || !name.trim() || (isMember && !nameDirty)}
            >
              {joining
                ? (isMember ? 'Saving…' : 'Joining…')
                : (isMember ? 'Save' : 'Join')}
            </button>
          </div>
          {!isMember && (
            <p className="lng-join-hint">
              Type a name and tap <strong>Join</strong>. Otherwise your score
              won't show up on this group's leaderboard.
            </p>
          )}
        </form>
      )}

      <section className="lng-leaderboard">
        <div className="lng-leaderboard-header">
          <h2 className="lng-leaderboard-title">Today</h2>
          {lastUpdatedAt && (
            <span className="lng-leaderboard-updated" aria-live="polite">
              {refreshing ? 'Refreshing…' : `Updated ${formatRelative(lastUpdatedAt)}`}
            </span>
          )}
        </div>
        <ol className="lng-leaderboard-list">
          {group.members.map((m, idx) => {
            const isMe = m.playerId === playerId;
            // For the current player's own row, prefer the locally-typed name
            // so renames feel instant (the server-stored name lags by one
            // /join round-trip and one fetchGroup). Other members still see
            // m.displayName from the server until the rename round-trips.
            const rowName = isMe ? (localTrimmed || m.displayName) : m.displayName;
            return (
              <li key={m.playerId} className={`lng-leaderboard-row${m.played ? ' is-played' : ' is-pending'}${isMe ? ' is-me' : ''}`}>
                <span className="lng-leaderboard-rank">{m.played ? idx + 1 : '·'}</span>
                <span className="lng-leaderboard-name">
                  {rowName}
                  {isMe && <span className="lng-leaderboard-you">you</span>}
                </span>
                <span className="lng-leaderboard-trailing">
                  <span className={`lng-leaderboard-score${m.played && m.won ? ' is-win' : m.played ? ' is-loss' : ''}`}>
                    {SCORE_LABEL(m)}
                  </span>
                  {!isMe && !m.played && m.nudgeable && (
                    <button
                      type="button"
                      className="lng-nudge-btn"
                      onClick={() => sendNudge(m.playerId)}
                      disabled={nudging === m.playerId}
                    >
                      {nudgeFeedback[m.playerId] || (nudging === m.playerId ? 'Nudging…' : 'Nudge')}
                    </button>
                  )}
                </span>
              </li>
            );
          })}
        </ol>
      </section>

      {group.standings.length > 0 && (
        <section className="lng-account-section">
          <h3 className="lng-modal-subtitle">Streaks</h3>
          <ul className="lng-account-list">
            {group.standings.map((s) => (
              <li key={s.playerId}>
                <span className="lng-history-row">
                  <span>{s.playerId === playerId ? `${s.displayName} (you)` : s.displayName}</span>
                  <strong>
                    {s.currentStreak > 0 ? `🔥 ${s.currentStreak}` : `${s.wins} win${s.wins === 1 ? '' : 's'}`}
                  </strong>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {error && <p className="lng-inline-error">{error}</p>}

      {!isMember && !user?.name && (
        <div className="lng-warning-card" role="status">
          <span className="lng-warning-icon" aria-hidden>!</span>
          <div className="lng-warning-body">
            <strong>You're viewing as a guest.</strong>{' '}
            Enter your name above and tap <strong>Join</strong> first —
            otherwise today's score won't appear on this leaderboard.
          </div>
        </div>
      )}

      <div className="lng-cta-row" style={{ marginTop: '1rem' }}>
        <Link
          to={`/langle/${difficulty}`}
          onClick={onPlayClick}
          className="lng-share-btn lng-share-primary"
          style={{ textAlign: 'center', textDecoration: 'none' }}
          aria-disabled={joining}
        >
          {!isMember && joining
            ? 'Joining…'
            : isMember
              ? "Play today's Langle"
              : "Join & play today's Langle"}
        </Link>
        <button
          className="lng-secondary-btn"
          onClick={() => httpBase && fetchGroup(httpBase, { manual: true })}
          disabled={refreshing || !httpBase}
        >
          {refreshing ? 'Refreshing…' : 'Refresh now'}
        </button>
      </div>
    </div>
  );
}

export default Group;
